/**
 * NearbyAmenitiesMap - Map overlay showing nearby amenities
 * Shows schools, hospitals, supermarkets, transport, etc.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { 
  School, Building2, ShoppingCart, Bus, 
  Utensils, Trees, Landmark, Church,
  MapPin, Loader2, X, ChevronDown, ChevronUp,
  Navigation, ExternalLink
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  schools: School,
  hospitals: Building2,
  supermarkets: ShoppingCart,
  transport: Bus,
  restaurants: Utensils,
  parks: Trees,
  banks: Landmark,
  worship: Church
};

// Category colors
const CATEGORY_COLORS = {
  schools: '#f59e0b',
  hospitals: '#ef4444',
  supermarkets: '#22c55e',
  transport: '#3b82f6',
  restaurants: '#f97316',
  parks: '#10b981',
  banks: '#8b5cf6',
  worship: '#6366f1'
};

// Create custom marker icon
const createAmenityIcon = (category) => {
  const color = CATEGORY_COLORS[category] || '#6b7280';
  
  return L.divIcon({
    className: 'custom-amenity-marker',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">
        <div style="
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

// Property marker icon
const propertyIcon = L.divIcon({
  className: 'property-marker',
  html: `
    <div style="
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      border: 3px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.5);
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Map center controller
const MapCenterController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
};

export default function NearbyAmenitiesMap({ 
  propertyLat, 
  propertyLon,
  propertyTitle = "Property",
  className = '' 
}) {
  const [amenities, setAmenities] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [radius, setRadius] = useState(1000);
  const [selectedCategories, setSelectedCategories] = useState([
    'schools', 'hospitals', 'supermarkets', 'transport'
  ]);
  const [showFilters, setShowFilters] = useState(true);
  
  // Fetch amenities when location or filters change
  useEffect(() => {
    if (!propertyLat || !propertyLon) return;
    
    const fetchAmenities = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const categoriesParam = selectedCategories.join(',');
        const response = await fetch(
          `${API_URL}/api/amenities/nearby?lat=${propertyLat}&lon=${propertyLon}&radius=${radius}&categories=${categoriesParam}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch amenities');
        }
        
        const data = await response.json();
        setAmenities(data.amenities || []);
        setCategoryCounts(data.categories || {});
      } catch (err) {
        setError(err.message);
        setAmenities([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Debounce the fetch
    const timer = setTimeout(fetchAmenities, 500);
    return () => clearTimeout(timer);
  }, [propertyLat, propertyLon, radius, selectedCategories]);
  
  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      }
      return [...prev, category];
    });
  };
  
  // Group amenities by category
  const groupedAmenities = useMemo(() => {
    const grouped = {};
    amenities.forEach(amenity => {
      if (!grouped[amenity.category]) {
        grouped[amenity.category] = [];
      }
      grouped[amenity.category].push(amenity);
    });
    return grouped;
  }, [amenities]);
  
  // All available categories
  const allCategories = ['schools', 'hospitals', 'supermarkets', 'transport', 'restaurants', 'parks', 'banks', 'worship'];
  
  return (
    <div className={`relative ${className}`} data-testid="nearby-amenities-map">
      {/* Filters Panel */}
      <Card className="absolute top-4 left-4 z-[1000] w-72 overflow-hidden">
        <button
          className="w-full p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-gray-800">Nearby Amenities</span>
          </div>
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {showFilters && (
          <div className="p-3 space-y-4">
            {/* Radius Slider */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <Label>Search Radius</Label>
                <span className="text-gray-600">{radius >= 1000 ? `${radius/1000}km` : `${radius}m`}</span>
              </div>
              <Slider
                value={[radius]}
                onValueChange={([v]) => setRadius(v)}
                min={500}
                max={3000}
                step={250}
                className="w-full"
              />
            </div>
            
            {/* Category Toggles */}
            <div className="space-y-2">
              <Label className="text-sm">Categories</Label>
              <div className="grid grid-cols-2 gap-2">
                {allCategories.map(category => {
                  const Icon = CATEGORY_ICONS[category];
                  const isSelected = selectedCategories.includes(category);
                  const count = categoryCounts[category]?.count || 0;
                  
                  return (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-xs transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" style={{ color: CATEGORY_COLORS[category] }} />
                      <span className="capitalize truncate">{category}</span>
                      {isSelected && count > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs px-1.5">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Loading/Error States */}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading amenities...
              </div>
            )}
            
            {error && (
              <div className="text-sm text-red-500">
                {error}
              </div>
            )}
            
            {/* Results Summary */}
            {!loading && !error && (
              <div className="text-xs text-gray-500">
                Found {amenities.length} amenities within {radius >= 1000 ? `${radius/1000}km` : `${radius}m`}
              </div>
            )}
          </div>
        )}
      </Card>
      
      {/* Map */}
      <MapContainer
        center={[propertyLat || 6.9271, propertyLon || 79.8612]}
        zoom={15}
        className="h-full w-full rounded-lg"
        zoomControl={false}
        style={{ zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapCenterController 
          center={[propertyLat, propertyLon]} 
          zoom={radius > 1500 ? 14 : 15} 
        />
        
        {/* Radius Circle */}
        <Circle
          center={[propertyLat, propertyLon]}
          radius={radius}
          pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '5, 5'
          }}
        />
        
        {/* Property Marker */}
        <Marker 
          position={[propertyLat, propertyLon]}
          icon={propertyIcon}
        >
          <Popup>
            <div className="text-center">
              <div className="font-semibold">{propertyTitle}</div>
              <div className="text-xs text-gray-500">Property Location</div>
            </div>
          </Popup>
        </Marker>
        
        {/* Amenity Markers */}
        {amenities.map(amenity => (
          <Marker
            key={amenity.id}
            position={[amenity.lat, amenity.lon]}
            icon={createAmenityIcon(amenity.category)}
          >
            <Popup>
              <div className="w-48">
                <div className="flex items-center gap-2 mb-1">
                  {React.createElement(CATEGORY_ICONS[amenity.category] || MapPin, {
                    className: "w-4 h-4",
                    style: { color: CATEGORY_COLORS[amenity.category] }
                  })}
                  <span className="font-semibold text-sm">{amenity.name}</span>
                </div>
                <Badge 
                  variant="secondary" 
                  className="text-xs mb-2"
                  style={{ backgroundColor: `${CATEGORY_COLORS[amenity.category]}20`, color: CATEGORY_COLORS[amenity.category] }}
                >
                  {amenity.category}
                </Badge>
                
                {amenity.tags?.type && (
                  <div className="text-xs text-gray-600 mb-1">
                    Type: {amenity.tags.type}
                  </div>
                )}
                
                {amenity.tags?.address && (
                  <div className="text-xs text-gray-600 mb-1">
                    📍 {amenity.tags.address}
                  </div>
                )}
                
                {amenity.tags?.opening_hours && (
                  <div className="text-xs text-gray-600 mb-2">
                    🕐 {amenity.tags.opening_hours}
                  </div>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-7 text-xs"
                  onClick={() => {
                    window.open(
                      `https://www.google.com/maps/dir/${propertyLat},${propertyLon}/${amenity.lat},${amenity.lon}`,
                      '_blank'
                    );
                  }}
                >
                  <Navigation className="w-3 h-3 mr-1" />
                  Get Directions
                </Button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-[1000]">
        <Card className="p-3">
          <div className="text-xs font-medium mb-2">Legend</div>
          <div className="space-y-1">
            {selectedCategories.slice(0, 4).map(category => {
              const Icon = CATEGORY_ICONS[category];
              return (
                <div key={category} className="flex items-center gap-2 text-xs">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: CATEGORY_COLORS[category] }}
                  />
                  <span className="capitalize">{category}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

// Compact amenities list component (for property detail page)
export function AmenitiesList({ propertyLat, propertyLon, radius = 1000 }) {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!propertyLat || !propertyLon) return;
    
    const fetchAmenities = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/amenities/nearby?lat=${propertyLat}&lon=${propertyLon}&radius=${radius}&categories=schools,hospitals,supermarkets,transport`
        );
        
        if (response.ok) {
          const data = await response.json();
          setAmenities(data.amenities || []);
        }
      } catch (err) {
        console.error('Failed to fetch amenities:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAmenities();
  }, [propertyLat, propertyLon, radius]);
  
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading nearby amenities...
      </div>
    );
  }
  
  // Group and limit
  const grouped = {};
  amenities.forEach(a => {
    if (!grouped[a.category]) grouped[a.category] = [];
    if (grouped[a.category].length < 3) grouped[a.category].push(a);
  });
  
  return (
    <div className="space-y-3" data-testid="amenities-list">
      {Object.entries(grouped).map(([category, items]) => {
        const Icon = CATEGORY_ICONS[category];
        return (
          <div key={category}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4" style={{ color: CATEGORY_COLORS[category] }} />
              <span className="text-sm font-medium capitalize">{category}</span>
              <Badge variant="secondary" className="text-xs">{items.length}</Badge>
            </div>
            <div className="space-y-1 pl-6">
              {items.map(item => (
                <div key={item.id} className="text-sm text-gray-600">
                  {item.name}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {Object.keys(grouped).length === 0 && (
        <div className="text-sm text-gray-500">No amenities found nearby</div>
      )}
    </div>
  );
}
